import GalleryPricingRedirect from "@/components/Gallery/GalleryPricingRedirect";
import type { RouteHandle } from "@/components/Root/hooks/useRouteHandle";
import { GALLERY_PRICING_PATH } from "@/data/urls";
import { createRouter, Navigate, RouteErrorBoundary } from "@/lib/router";
import { GalleryLayout, PublicLayout } from "./layouts";
import AppLayout from "./layouts/AppLayout";
import SharedLayout from "./layouts/SharedLayout";
import { DashboardPage } from "./pages/dashboard.protected";

export function createAppRouter() {
  return createRouter([
    // PUBLIC ROUTES (no auth required, guest-only pages)
    {
      element: <PublicLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          path: "/auth",
          handle: { hideIntercom: true } satisfies RouteHandle,
          lazy: () =>
            import("./pages/auth.public").then((m) => ({
              Component: m.AuthPage,
            })),
        },
        {
          path: "/login",
          handle: { hideIntercom: true } satisfies RouteHandle,
          lazy: () =>
            import("./pages/login.public").then((m) => ({
              Component: m.LoginPage,
            })),
        },
        {
          path: "/signup",
          handle: { hideIntercom: true } satisfies RouteHandle,
          lazy: () =>
            import("./pages/signup.public").then((m) => ({
              Component: m.SignupPage,
            })),
        },
        {
          path: "/forgot-password",
          lazy: () =>
            import("./pages/forgot-password.public").then((m) => ({
              Component: m.ForgotPasswordPage,
            })),
        },
        {
          path: "/query",
          lazy: () =>
            import("./pages/query.public").then((m) => ({
              Component: m.QueryPage,
            })),
        },
        {
          path: "/open-link",
          lazy: () =>
            import("./pages/open-link.public").then((m) => ({
              Component: m.OpenLinkPage,
            })),
        },

        {
          path: "/referral-signup",
          handle: { hideIntercom: true } satisfies RouteHandle,
          lazy: () =>
            import("./pages/referral-signup.public").then((m) => ({
              Component: m.ReferralSignupPage,
            })),
        },
        {
          path: "/signup-mastin",
          handle: { hideIntercom: true } satisfies RouteHandle,
          lazy: () =>
            import("./pages/signup-mastin.public").then((m) => ({
              Component: m.SignUpMastinPage,
            })),
        },
        {
          path: "/start-trial-now",
          lazy: () =>
            import("./pages/start-trial-now.public").then((m) => ({
              Component: m.StartTrialNowPage,
            })),
        },
        {
          path: "/convertertool-signup",
          lazy: () =>
            import("./pages/converter-tool.public").then((m) => ({
              Component: m.ConverterToolPage,
            })),
        },
        {
          path: "/auto-auth",
          lazy: () =>
            import("./pages/auto-auth.public").then((m) => ({
              Component: m.AutoAuthPage,
            })),
        },

        {
          path: "/payment-successful",
          lazy: () =>
            import("./pages/payment-success.public").then((m) => ({
              Component: m.PaymentSuccessPage,
            })),
        },
        {
          path: "/next-login",
          handle: { hideIntercom: true } satisfies RouteHandle,
          lazy: () =>
            import("./pages/next-login.public").then((m) => ({
              Component: m.NextAppLogin,
            })),
        },
        {
          path: "/photon-login",
          handle: { hideIntercom: true } satisfies RouteHandle,
          lazy: () =>
            import("./pages/photon-login.public").then((m) => ({
              Component: m.PhotonAppLogin,
            })),
        },
        {
          path: "/__error-test",
          element: <RouteErrorBoundary />,
        },
        {
          path: "*",
          lazy: () =>
            import("./pages/not-found.public").then((m) => ({
              Component: m.NotFoundPage,
            })),
        },
      ],
    },

    // SHARED ROUTES (accessible by both authenticated and unauthenticated users)
    {
      element: <SharedLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          path: "/pricing",
          lazy: () =>
            import("./pages/pricing.share").then((m) => ({
              Component: m.PricingPage,
            })),
        },
      ],
    },

    // APP ROUTES (auth required)
    {
      element: <AppLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          path: "/",
          element: <DashboardPage />,
        },
        {
          path: "/review",
          element: <Navigate to="/" replace />,
        },
        {
          path: "/checkout",
          lazy: () =>
            import("./pages/checkout").then((m) => ({
              Component: m.CheckoutPage,
            })),
        },
        {
          path: "/seat-management",
          lazy: () =>
            import("./pages/seat-management.protected").then((m) => ({
              Component: m.SeatManagementPage,
            })),
        },
        {
          path: "/manage-devices",
          lazy: () =>
            import("./pages/manage-devices.protected").then((m) => ({
              Component: m.ManageDevicesPage,
            })),
        },
        {
          path: "/retrain",
          lazy: () =>
            import("./pages/retrain.protected").then((m) => ({
              Component: m.RetrainPage,
            })),
        },

        {
          path: "/open-next",
          lazy: () =>
            import("./pages/open-aftershoot-next.protected").then((m) => ({
              Component: m.OpenAftershootNext,
            })),
        },
        {
          path: "/open-photon",
          handle: { hideIntercom: true } satisfies RouteHandle,
          lazy: () =>
            import("./pages/open-photon.protected").then((m) => ({
              Component: m.OpenPhoton,
            })),
        },
        {
          path: "/referral",
          lazy: () =>
            import("./pages/referral.protected").then((m) => ({
              Component: m.ReferralPage,
            })),
        },
        {
          path: "/manage-profiles",
          lazy: () =>
            import("./pages/manage-profiles.protected").then((m) => ({
              Component: m.ManageProfilesPage,
            })),
        },
        {
          path: "/auto-login",
          lazy: () =>
            import("./pages/auto-login.protected").then((m) => ({
              Component: m.AutoLoginPage,
            })),
        },
        // {
        //   path: "/welcome",
        //   lazy: () =>
        //     import("./pages/_welcome").then((m) => ({
        //       Component: m.WelcomePage,
        //     })),
        // },
        {
          path: "/purchase-profile",
          lazy: () =>
            import("./pages/profile-purchase.protected").then((m) => ({
              Component: m.ProfilePurchasePage,
            })),
        },
        {
          path: "/plan-cancel",
          lazy: () =>
            import("./pages/cancellation.protected").then((m) => ({
              Component: m.CancellationPage,
            })),
        },
        {
          path: "/cancellation",
          lazy: () =>
            import("./pages/cancellation.protected").then((m) => ({
              Component: m.CancellationPage,
            })),
        },
        {
          path: "/manage-subscription",
          lazy: () =>
            import("./pages/manage-subscription.protected").then((m) => ({
              Component: m.ManageSubscriptionPage,
            })),
        },
        {
          path: "/cancel-cs",
          lazy: () =>
            import("./pages/cancel-cs.protected").then((m) => ({
              Component: m.CancelCSPage,
            })),
        },
        {
          path: "/pw-update",
          lazy: () =>
            import("./pages/pw-update.private").then((m) => ({
              Component: m.PasswordUpdatePage,
            })),
        },
      ],
    },
    // GALLERY ROUTES
    {
      path: "/gallery",
      element: <GalleryLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          index: true,
          element: <Navigate to={GALLERY_PRICING_PATH} replace />,
        },
        {
          // Gallery pricing now lives on galleries.aftershoot.com
          path: "pricing",
          element: <GalleryPricingRedirect />,
        },
      ],
    },
  ]);
}
